/* eslint-disable import/no-extraneous-dependencies */
/* eslint-disable class-methods-use-this */
import axios from 'axios';
import store from '@/store';
import Vuex from 'vuex';
import { cloneDeep } from 'lodash';
import nodesType from '@/utilities/enums/nodesTypes';
import showError from '@/store/modules/errorHandler';
import io from 'socket.io-client';
import ChatConstants from '@/constants/chatHelper';
import { initSocketClient } from '@/utilities/socket';
// import rolesImport from '../constants/roles';

const { SOCKET_EVENTS } = ChatConstants;


const { CancelToken } = axios; // cancel request related
const source = CancelToken.source(); // cancel request related

let isRefreshing = false;
let refreshPromise = null;

// requests private methods
function getNodeType(nodeType) {
    let nodeEndpoint = '';
    switch (nodeType.toLowerCase()) {
        case nodesType.site:
            nodeEndpoint = '/sites';
            break;
        case nodesType.container:
            nodeEndpoint = '/containers';
            break;
        case nodesType.network:
            nodeEndpoint = '/networks';
            break;
        case nodesType.component:
            nodeEndpoint = '/components';
            break;
        default:
    }

    return nodeEndpoint;
}

class AqAjaxRequests {
    // eslint-disable-next-line no-unused-vars
    async initSocket(currentRole) {
        // if (![roles.ADMINISTRATOR, roles.MANAGER].includes(currentRole)) {
        // console.log('Current role does not have permission to initialize the WebSocket');
        // return '';
        // }
        if (this.socket && this.socket.connected) {
            console.log('Socket is already initialized');
            return this.socket;
        }

        if (!this.socket) {
            await this.initializeAndConnectSocket();
        } else {
            this.reconnectWithBackoff();
        }

        return new Promise((resolve, reject) => {
            if (this.socket.connected) {
                resolve(this.socket);
            } else {
                this.socket.once('connect', () => resolve(this.socket));
                this.socket.once('connect_error', error => reject(error));
            }
        });
    }

    async initializeAndConnectSocket() {
        try {
            const response = await this.getChatApiInfo();
            const { endpointRTM, tokenId, tokenSecret } = response.data;
            const endpoint = new URL(endpointRTM);
            this.socket = io(endpoint.origin, {
                path: endpoint.pathname || '/',
                transports: ['websocket'],
            });

            this.setupSocketListeners(tokenId, tokenSecret);
        } catch (error) {
            console.error('Failed to initialize and connect socket:', error);
            throw error;
        }
    }

    setupSocketListeners(tokenId, tokenSecret) {
        this.socket.on('connect', () => {
            console.log('RTM API connected');
            this.authenticateSocket(tokenId, tokenSecret);
            this.reconnectionAttempts = 0;
        });

        this.socket.on('disconnect', () => {
            console.log('RTM API disconnected');
            this.reconnectWithBackoff();
        });

        this.socket.on('reconnect', () => {
            console.log('RTM API reconnected');
            this.authenticateSocket(tokenId, tokenSecret);
        });

        this.socket.on('connect_error', error => {
            console.log('RTM API connection error', error);
        });

        this.socket.on('error', error => {
            console.error('RTM API error', error);
        });
    }

    reconnectWithBackoff(maxAttempts = 5) {
        const attemptReconnection = () => {
            if (this.reconnectionAttempts < maxAttempts) {
                setTimeout(() => {
                    if (!this.socket.connected) {
                        console.log(`Attempting to reconnect (${this.reconnectionAttempts + 1}/${maxAttempts})...`);
                        this.reconnectionAttempts += 1;
                        this.socket.connect();
                        attemptReconnection();
                    }
                }, this.calculateExponentialBackoff(this.reconnectionAttempts));
            } else {
                console.log('Max reconnection attempts reached. Stopping attempts to reconnect.');
            }
        };

        attemptReconnection();
    }

    calculateExponentialBackoff(attempts, baseDelay = 1000) {
        return Math.min(30000, baseDelay * (2 ** attempts));
    }

    authenticateSocket(tokenId, tokenSecret) {
        if (!this.socket) {
            console.error('Socket is not initialized');
            return;
        }

        this.socket.emit('authentication', {
            username: tokenId,
            password: tokenSecret,
            events: Object.values(SOCKET_EVENTS),
        });
    }

    init() {
        this.socket = null;
        this.reconnectionAttempts = 0;
        this.appId = process.env.VUE_APP_APP_ID;
        this.appVersion = process.env.PACKAGE_VERSION;
        this.appPlatform = 'web';
        this.url = process.env.VUE_APP_API_URL;
        this.axiosRequest = axios.create({
            baseURL: this.url,
        });
        this.axiosAuthentifiedRequest = axios.create({
            baseURL: this.url,
        });
        this.setAxiosHeaders({
            'Content-Type': 'application/json',
            'Cache-Control': 'no-cache',
            'X-AQ-App-Identifier': this.appId,
            'X-AQ-App-Version': this.appVersion,
            'X-AQ-Platform': this.appPlatform,
            'X-AQ-Locale-Identifier': store.lang,
        });

        const accessToken = window.$cookies.get('accessToken');

        if (accessToken) {
            this.axiosAuthentifiedRequest.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
        }

        this.axiosAuthentifiedRequest.interceptors.request.use(
            async request => {
                axios.defaults.headers.common['X-AQ-Locale-Identifier'] = store.getters.lang;
                if (window.$cookies.isKey('accessToken')) {
                    // eslint-disable-next-line no-param-reassign
                    request.headers.Authorization = `Bearer ${window.$cookies.get(
                        'accessToken',
                    )}`;

                    return request;
                }

                await this.refreshAccessTokenRequestAndResponseInterceptor();

                // eslint-disable-next-line no-param-reassign
                request.headers.Authorization = `Bearer ${window.$cookies.get(
                    'accessToken',
                )}`;

                return request;
            },
        );

        this.axiosAuthentifiedRequest.interceptors.response.use(
            response => response,
            async error => {
                if (!axios.isCancel(error)) {
                    if (!error || error.response.status !== 401) {
                        if (error.response.data?.error?.code === 1002) {
                            return Promise.reject(error);
                        }
                        return showError(error);
                    }
                    // FIXME : in case of bad token, this is looping over and over again
                    await this.refreshAccessTokenRequestAndResponseInterceptor();
                    // eslint-disable-next-line no-param-reassign
                    error.config.headers.Authorization = `Bearer ${window.$cookies.get(
                        'accessToken',
                    )}`;
                    return this.axiosAuthentifiedRequest.request(
                        error.config,
                    );
                }
                return null;
            },
        );
        // Automatically show errors if not authenticated
        this.axiosRequest.interceptors.response.use(
            response => response,
            error => showError(error),
        );
    }


    refreshAccessTokenRequestAndResponseInterceptor() {
        // If already refreshing, return the existing promise to prevent duplicate calls
        if (isRefreshing) {
            return refreshPromise;
        }

        if (window.$cookies.get('refreshToken')) {
            isRefreshing = true;
            refreshPromise = store
                .dispatch('refreshAccessTokenAndReloadAccessControlState')
                .then(result => {
                    isRefreshing = false;
                    return result;
                })
                .catch(err => {
                    isRefreshing = false;
                    console.log(
                        'error while refreshing access token and reloading access control state',
                        err,
                    );
                    return store
                        .dispatch('logout')
                        .then(() => window.location.reload());
                });

            return refreshPromise;
        }

        return store.dispatch('logout').then(() => window.location.reload());
    }

    setAxiosHeaders(headers) {
        const headerKeys = Object.keys(headers);
        headerKeys.forEach(value => {
            this.axiosRequest.defaults.headers.common[value] = headers[value];
            this.axiosAuthentifiedRequest.defaults.headers.common[value] = headers[value];
        });
    }

    resetAxiosHeaders() {
        Object.keys(axios.defaults.headers.common).forEach(headerKey => {
            delete this.axiosRequest.defaults.headers.common[headerKey];
            delete this.axiosAuthentifiedRequest.defaults.headers.common[
                headerKey
            ];
        });
        delete this.axiosAuthentifiedRequest.defaults.headers.common
            .Authorization;
    }

    async initWebSockets() {
        await initSocketClient();
    }

    getRenewToken() {
        const requestObject = this;

        return new Promise((resolve, reject) => {
            const data = {};
            data.refreshToken = window.$cookies.get('refreshToken');
            data.rtype = 'auth';

            if (store.getters.teamID || window.$cookies.get('cypressTeamId')) {
                data.teamId = window.$cookies.get('cypressTeamId') ? window.$cookies.get('cypressTeamId') : store.getters.teamID;
            }

            const tokenEndpoint = `${requestObject.url}/auth/token`;
            if (window.Cypress) {
                return;
            }

            const axiosWithoutInterceptors = axios.create({
                baseURL: requestObject.url,
            });

            axiosWithoutInterceptors
                .post(tokenEndpoint, data, {
                    headers: {
                        Authorization: `Bearer ${window.$cookies.get('refreshToken')}`,
                    },
                })
                .then(response => {
                    if (typeof response !== 'undefined') {
                        // Passes
                        store.commit({
                            type: 'setAccessToken',
                            token: response.data.accessToken,
                            secured: process.env.NODE_ENV !== 'development',
                        });
                        resolve(response.data.accessToken);
                        this.initWebSockets();
                    } else {
                        throw new Error({ message: 'Error, invalid token' });
                    }
                })
                .catch(error => {
                    store.dispatch('logout').then(() => {
                        window.location.reload();
                    });
                    showError(error);
                    reject(error);
                });
        });
    }

    getProfile() {
        const endpoint = '/users/me/profile';
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    getVerificationCodeStatus(verificationCode, userId) {
        const endpoint = `/users/${userId}/verification/verified?verificationcode=${verificationCode}`;
        return this.axiosRequest.get(endpoint);
    }

    login(user) {
        const endpoint = '/auth/login';
        return this.axiosRequest.post(endpoint, user);
    }

    loginSso(payload) {
        const endpoint = '/sso';
        return this.axiosAuthentifiedRequest.post(endpoint, payload);
    }

    postChangePassword(password) {
        const endpoint = '/auth/password/change';
        return this.axiosAuthentifiedRequest.post(endpoint, password);
    }

    postRequestResetPassword(email) {
        const endpoint = '/auth/password/reset/request';
        return this.axiosRequest.post(endpoint, email);
    }

    postConfirmPasswordReset(passwordInfos) {
        const endpoint = '/auth/password/reset/confirm';
        return this.axiosRequest.post(endpoint, passwordInfos);
    }

    postInviteUser(teamId, user) {
        const endpoint = `/teams/${teamId}/members/invite`;
        return this.axiosAuthentifiedRequest.post(endpoint, user);
    }

    resendInvitation(teamId, queryParams) {
        const endpoint = `/teams/${teamId}/members/invite/resend`;
        return this.axiosAuthentifiedRequest.post(endpoint, null, {
            params: queryParams,
        });
    }


    postUserSignup(userData) {
        const endpoint = `/users/${userData.id}/verification/confirm?verificationcode=${userData.verificationCode}`;

        return this.axiosRequest.post(endpoint, {
            firstName: userData.firstName,
            lastName: userData.lastName,
            email: userData.email,
            password: userData.password,
            locale: userData.locale,
        });
    }

    updateProfile(userId, updatesData) {
        const endpoint = `/users/${userId}/profile`;
        return this.axiosAuthentifiedRequest.put(endpoint, updatesData);
    }

    getRelease(queryParams) {
        const endpoint = '/release';
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }

    updateRelease(params) {
        const endpoint = '/release';
        return this.axiosAuthentifiedRequest.patch(endpoint, params);
    }

    getReportTypes(params = { pagination: false }) {
        const endpoint = '/reports/types';
        return this.axiosAuthentifiedRequest.get(endpoint, { params });
    }

    getReportType(typeId) {
        const endpoint = `/reports/types/${typeId}`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    addReportType(bodyParams) {
        const endpoint = '/reports/types';
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams);
    }

    updateReportType(typeId, bodyParams) {
        const endpoint = `/reports/types/${typeId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    removeReportType(typeId) {
        const endpoint = `/reports/types/${typeId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    getDocumentTypes(params = { pagination: false }) {
        const endpoint = '/documents/types';
        return this.axiosAuthentifiedRequest.get(endpoint, { params });
    }

    getDocumentType(typeId) {
        const endpoint = `/documents/types/${typeId}`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    addDocumentType(bodyParams) {
        const endpoint = '/documents/types';
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams);
    }

    updateDocumentType(typeId, bodyParams) {
        const endpoint = `/documents/types/${typeId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    removeDocumentType(typeId) {
        const endpoint = `/documents/types/${typeId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    getWorkOrderTypes(params = { pagination: false }) {
        const endpoint = '/workorders/types';
        return this.axiosAuthentifiedRequest.get(endpoint, { params });
    }

    getWorkOrderType(typeId) {
        const endpoint = `/workorders/types/${typeId}`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    addWorkOrderType(bodyParams) {
        const endpoint = '/workorders/types';
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams);
    }

    updateWorkOrderType(typeId, bodyParams) {
        const endpoint = `/workorders/types/${typeId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    removeWorkOrderType(typeId) {
        const endpoint = `/workorders/types/${typeId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    getTables(activityId) {
        const endpoint = `/activities/${activityId}/summary/tables`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    getDevicesByNetwork(activityId) {
        const endpoint = `/activities/${activityId}/summary/devices`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    updateComponentNote(activityId, tableId, componentId, bodyParams) {
        const endpoint = `/activities/${activityId}/summary/tables/${tableId}/components/${componentId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    updateMeasureNote(activityId, tableId, measureId, bodyParams) {
        const endpoint = `/activities/${activityId}/summary/tables/${tableId}/measures/${measureId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    getLegionellaRiskFactors() {
        const endpoint = '/legionella/riskfactors';
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    getLegionellaRiskAssessments(queryParams) {
        const endpoint = '/legionella/riskassessments';
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }

    getLegionellaRiskAssessment(assessmentId) {
        const endpoint = `/legionella/riskassessments/${assessmentId}`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    addLegionellaRiskAssessment(bodyParams) {
        const endpoint = '/legionella/riskassessments';
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams);
    }

    updateLegionellaRiskAssessment(assessmentId, bodyParams) {
        const endpoint = `/legionella/riskassessments/${assessmentId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    getSiteDocuments(siteId, queryParams) {
        const endpoint = `/sites/${siteId}/documents`;
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }

    getSiteDocument(siteId, documentId, queryParams) {
        const endpoint = `/sites/${siteId}/documents/${documentId}`;
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }

    addSiteDocument(siteId, bodyParams) {
        const endpoint = `/sites/${siteId}/documents`;
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
    }

    updateSiteDocument(siteId, documentId, bodyParams) {
        const endpoint = `/sites/${siteId}/documents/${documentId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
    }

    deleteSiteDocument(siteId, documentId) {
        const endpoint = `/sites/${siteId}/documents/${documentId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    getSiteReports(siteId, queryParams) {
        const endpoint = `/sites/${siteId}/reports`;
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }

    getLabReports(queryParams) {
        const endpoint = '/lab/reports';
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }

    getClientsLabReports(queryParams) {
        const endpoint = '/lab/reports/clients';
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }

    transferSiteToAnotherClient(siteId, clientId) {
        const endpoint = `/sites/${siteId}/client/${clientId}/transfer`;
        return this.axiosAuthentifiedRequest.patch(endpoint);
    }

    getTreatmentPrograms(networkId, queryParams) {
        const endpoint = `/networks/${networkId}/treatments`;
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }

    addTreatmentProgram(networkId, bodyParams) {
        const endpoint = `/networks/${networkId}/treatments`;
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams);
    }

    updateTreatmentProgram(networkId, treatmentProgramId, bodyParams) {
        const endpoint = `/networks/${networkId}/treatments/${treatmentProgramId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    removeTreatmentProgram(networkId, treatmentProgramId) {
        const endpoint = `/networks/${networkId}/treatments/${treatmentProgramId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    removeSteamTreatmentProgram(networkId, treatmentProgramId) {
        const endpoint = `/networks/${networkId}/steamTreatments/${treatmentProgramId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    getSteamTreatmentPrograms(networkId, queryParams) {
        const endpoint = `/networks/${networkId}/steamTreatments`;
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }

    addSteamTreatmentProgram(networkId, bodyParams) {
        const endpoint = `/networks/${networkId}/steamTreatments`;
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams);
    }

    updateSteamTreatmentProgram(networkId, treatmentProgramId, bodyParams) {
        const endpoint = `/networks/${networkId}/steamTreatments/${treatmentProgramId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    getTreatmentControls(params = { pagination: false }) {
        const endpoint = '/treatmentcontrols';
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params,
        });
    }

    getTreatmentControl(treatmentControlId) {
        const endpoint = `/treatmentcontrols/${treatmentControlId}`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    addTreatmentControl(bodyParams) {
        const endpoint = '/treatmentcontrols';
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams);
    }

    updateTreatmentControl(treatmentControlId, bodyParams) {
        const endpoint = `/treatmentcontrols/${treatmentControlId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    removeTreatmentControl(treatmentControlId) {
        const endpoint = `/treatmentcontrols/${treatmentControlId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    getProcedures(queryParams) {
        const endpoint = '/procedures';
        return this.axiosAuthentifiedRequest.get(endpoint, { params: queryParams });
    }

    getProcedure(procedureId) {
        const endpoint = `/procedures/${procedureId}`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    addProcedure(bodyParams) {
        const endpoint = '/procedures';
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams);
    }

    updateProcedure(procedureId, bodyParams) {
        const endpoint = `/procedures/${procedureId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    deleteProcedure(procedureId) {
        const endpoint = `/procedures/${procedureId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    getFrequencies(queryParams) {
        const endpoint = '/maintenanceschedule/frequencies';
        return this.axiosAuthentifiedRequest.get(endpoint, { params: queryParams });
    }

    addFrequency(bodyParams) {
        const endpoint = '/maintenanceschedule/frequencies';
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams);
    }

    updateFrequency(frequencyId, bodyParams) {
        const endpoint = `/maintenanceschedule/frequencies/${frequencyId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    removeFrequency(frequencyId) {
        const endpoint = `/maintenanceschedule/frequencies/${frequencyId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    getActivities(queryParams) {
        const endpoint = '/maintenanceschedule/activities';
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }

    addActivity(bodyParams) {
        const endpoint = '/maintenanceschedule/activities';
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams);
    }

    updateActivity(activityId, bodyParams) {
        const endpoint = `/maintenanceschedule/activities/${activityId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    removeActivity(activityId) {
        const endpoint = `/maintenanceschedule/activities/${activityId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    updateActivitiesPosition(activityId, bodyParams) {
        const endpoint = `/maintenanceschedule/activities/${activityId}/reorder`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    getScheduleTemplate(queryParams) {
        const endpoint = '/maintenanceschedule/template';
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }

    getSchedules(queryParams) {
        const endpoint = '/maintenanceschedule/schedules';
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }

    getSchedule(scheduleId) {
        const endpoint = `/maintenanceschedule/schedules/${scheduleId}`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    addSchedule(bodyParams) {
        const endpoint = '/maintenanceschedule/schedules';
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams);
    }

    updateSchedule(scheduleId, bodyParams) {
        const endpoint = `/maintenanceschedule/schedules/${scheduleId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    getMaintenanceScheduleCategories(queryParams) {
        const endpoint = '/maintenanceschedule/categories';
        return this.axiosAuthentifiedRequest.get(endpoint,
            { params: queryParams });
    }

    addMaintenanceScheduleCategory(bodyParams) {
        const endpoint = '/maintenanceschedule/categories';
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams);
    }

    updateMaintenanceScheduleCategory(categoryId, bodyParams) {
        const endpoint = `/maintenanceschedule/categories/${categoryId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    removeMaintenanceScheduleCategory(categoryId) {
        const endpoint = `/maintenanceschedule/categories/${categoryId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    getProductInventory(siteId, productId, queryParams) {
        const endpoint = `/sites/${siteId}/products/${productId}/inventory`;
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }

    getProductInventoryItem(siteId, productId, inventoryId, queryParams) {
        const endpoint = `/sites/${siteId}/products/${productId}/inventory/${inventoryId}`;
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }

    addProductInventoryItem(siteId, productId, bodyParams) {
        const endpoint = `/sites/${siteId}/products/${productId}/inventory`;
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams);
    }

    updatedProductInventoryItem(siteId, productId, inventoryId, bodyParams) {
        const endpoint = `/sites/${siteId}/products/${productId}/inventory/${inventoryId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    removeProductInventoryItem(siteId, productId, inventoryId) {
        const endpoint = `/sites/${siteId}/products/${productId}/inventory/${inventoryId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    getProductContainers(bodyParams) {
        const endPoint = '/productcontainers';
        return this.axiosAuthentifiedRequest.get(endPoint, { params: bodyParams });
    }

    getProductContainer(productContainerId) {
        const endPoint = `/productcontainers/${productContainerId}`;
        return this.axiosAuthentifiedRequest.get(endPoint);
    }

    addProductContainer(bodyParams) {
        const endPoint = '/productcontainers';
        return this.axiosAuthentifiedRequest.post(endPoint, bodyParams);
    }

    updateProductContainer(productContainerId, bodyParams) {
        const endPoint = `/productcontainers/${productContainerId}`;
        return this.axiosAuthentifiedRequest.patch(endPoint, bodyParams);
    }

    deleteProductContainer(productContainerId) {
        const endPoint = `/productcontainers/${productContainerId}`;
        return this.axiosAuthentifiedRequest.delete(endPoint);
    }

    getStandardFamilies(queryParams) {
        const endpoint = '/standardsfamilies';
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }

    getStandardFamily(standardFamilyId) {
        const endpoint = `/standardsfamilies/${standardFamilyId}`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    addStandardFamily(bodyParams) {
        const endpoint = '/standardsfamilies';
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams);
    }

    updateStandardFamily(standardFamilyId, bodyParams) {
        const endpoint = `/standardsfamilies/${standardFamilyId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    deleteStandardFamily(standardFamilyId) {
        const endpoint = `/standardsfamilies/${standardFamilyId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    getStandards(queryParams) {
        const endpoint = '/standards';
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }

    addStandard(bodyParams) {
        const endpoint = '/standards';
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams);
    }

    updateStandard(standardId, bodyParams) {
        const endpoint = `/standards/${standardId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    deleteStandard(standardId) {
        const endpoint = `/standards/${standardId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    getSiteChangeEvents(siteId, clientId, queryParams = {}) {
        const endpoint = `/logs/changeevents/sites/${siteId}/clients/${clientId}`;
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }

    getChangeEvents(queryParams = {}) {
        const endpoint = '/logs/changeevents/admin';
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }


    addFile(params, file) {
        const endpoint = '/files';
        return this.axiosAuthentifiedRequest.post(endpoint, file, {
            params,
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
    }

    storeS3File(params, file) {
        const endpoint = '/files/s3';
        return this.axiosAuthentifiedRequest.post(endpoint, file, {
            params,
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
    }

    getS3File(config) {
        const endpoint = '/files/s3';
        return this.axiosAuthentifiedRequest.get(endpoint, config);
    }

    deleteFile(fileId) {
        const endpoint = `/files/${fileId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    getLabWorkOrderComments(labWorkOrderId) {
        const endpoint = `/lab/workorders/${labWorkOrderId}/comments`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    addLabWorkOrderComment(labWorkOrderId, bodyParams) {
        const endpoint = `/lab/workorders/${labWorkOrderId}/comments`;
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams);
    }

    updateLabWorkOrderComment(labWorkOrderId, commentId, bodyParams) {
        const endpoint = `/lab/workorders/${labWorkOrderId}/comments/${commentId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    removeLabWorkOrderComment(labWorkOrderId, commentId) {
        const endpoint = `/lab/workorders/${labWorkOrderId}/comments/${commentId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    removeFilesToALabComment(commentId, labWorkOrderId, fileId) {
        const endpoint = `/lab/workorders/${labWorkOrderId}/comments/${commentId}/files/${fileId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    getComments(workOrderId) {
        const endpoint = `/workorders/${workOrderId}/comments`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    addComment(workOrderId, bodyParams) {
        const endpoint = `/workorders/${workOrderId}/comments`;
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams);
    }

    editComment(workOrderId, commentId, bodyParams) {
        const endpoint = `/workorders/${workOrderId}/comments/${commentId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    deleteComment(workOrderId, commentId) {
        const endpoint = `/workorders/${workOrderId}/comments/${commentId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    addFileToAComment(commentId, bodyParams) {
        const endpoint = `/workorders/${commentId}/files`;
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    }

    removeFilesToAComment(commentId, workOrderId, fileId) {
        const endpoint = `/workorders/${workOrderId}/comments/${commentId}/files/${fileId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    getMemberInfo(memberId) {
        const endpoint = `/members/${memberId}`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    updateMember(memberId, params) {
        const endpoint = `/members/${memberId}/updateDisplayNote`;
        return this.axiosAuthentifiedRequest.patch(endpoint, params);
    }

    updateMemberDisplayTerms(memberId, payload) {
        const endpoint = `/members/${memberId}/updateDisplayTerms`;
        return this.axiosAuthentifiedRequest.patch(endpoint, payload);
    }

    acknowledgeMemberTerms(memberId, payload) {
        const endpoint = `/members/${memberId}/termsOfUse`;
        return this.axiosAuthentifiedRequest.patch(endpoint, payload);
    }

    getMemberClients(memberId, queryParams) {
        const endpoint = `/members/${memberId}/clients`;
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }

    getMemberSites(memberId, queryParams) {
        const endpoint = `/members/${memberId}/sites`;
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }

    getMemberAccess(memberId, queryParams) {
        const endpoint = `/members/${memberId}/access`;
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }

    updateMemberRole(memberId, roles) {
        const endpoint = `/members/${memberId}`;
        return this.axiosAuthentifiedRequest.put(endpoint, roles);
    }

    updateClient(clientId, bodyParams) {
        const endpoint = `/clients/${clientId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    associateMemberToClient(memberId, bodyParams) {
        const endpoint = `/members/${memberId}/clients/`;
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams);
    }

    associateMemberToSite(memberId, bodyParams) {
        const endpoint = `/members/${memberId}/sites/`;
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams);
    }

    removeMemberFromTeam(memberId) {
        const endpoint = `/members/${memberId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    revokeClientToMember(memberId, clientId) {
        const endpoint = `/members/${memberId}/clients/${clientId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    revokeSiteToMember(memberId, siteId) {
        const endpoint = `/members/${memberId}/sites/${siteId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    //--------------------------------------------------------------------
    // ---- ACCESS GROUPS ENDPOINTS ---------------------------------------
    // --------------------------------------------------------------------
    getAccessGroups(queryParams) {
        const endpoint = '/accessgroups';
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }

    getAccessGroup(accessGroupId, queryParams) {
        const endpoint = `/accessgroups/${accessGroupId}`;
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }

    addAccessGroup(bodyParams) {
        const endpoint = '/accessgroups';
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams);
    }

    updateAccessGroup(accessGroupId, bodyParams) {
        const endpoint = `/accessgroups/${accessGroupId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    deleteAccessGroup(accessGroupId) {
        const endpoint = `/accessgroups/${accessGroupId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    updateMemberAccessGroup(memberId, bodyParams) {
        const endpoint = `/members/${memberId}/accessgroups/`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    UpdateCanAccessToAllClients(memberId, bodyParams) {
        const endPoint = `/members/${memberId}/accessallclients`;
        return this.axiosAuthentifiedRequest.patch(endPoint, bodyParams);
    }

    updateClientAccessGroup(clientId, bodyParams) {
        const endpoint = `/clients/${clientId}/accessgroups/`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    getClientAccessGroup(clientId, bodyParams) {
        const endpoint = `/clients/${clientId}/accessgroups/`;
        return this.axiosAuthentifiedRequest.get(endpoint, bodyParams);
    }

    updateSignature(memberId, bodyParams) {
        const endpoint = `/members/${memberId}/signature`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    addSignatureFile(memberId, file) {
        const endpoint = `/members/${memberId}/signature/file`;
        return this.axiosAuthentifiedRequest.post(endpoint, file, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
    }

    deleteSignatureFile(memberId) {
        const endpoint = `/members/${memberId}/signature/file`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    addTeam(bodyParams) {
        const endpoint = '/teams';
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams);
    }

    updateTeam(teamId, bodyParams) {
        const endpoint = `/teams/${teamId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    removeTeam(teamId) {
        const endpoint = `/teams/${teamId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    getTeam(teamId) {
        const endpoint = `/teams/${teamId}`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    getTeams(params = { pagination: false }) {
        const endpoint = '/teams';
        return this.axiosAuthentifiedRequest.get(endpoint, { params });
    }

    getTeamsAndUsersCount(params) {
        const endpoint = '/teams/teamsUsersCount';
        return this.axiosAuthentifiedRequest.get(endpoint, { params });
    }

    getUserTeams() {
        const endpoint = '/users/me/teams';
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    getTeamMembers(teamId, role, extraParams = {}) {
        const endpoint = `/teams/${teamId}/members`;
        let params;

        if (Array.isArray(role)) {
            params = { role: role.join(','), ...extraParams };
        } else {
            const initialParams = role === 'team' ? {} : { role };
            params = { ...initialParams, ...extraParams };
        }

        return this.axiosAuthentifiedRequest.get(endpoint, { params });
    }


    postAcceptTeamInvitation(teamId, inviteCode) {
        const endpoint = `/teams/${teamId}/members/invite/accept?invitecode=${inviteCode}`;
        return this.axiosRequest.post(endpoint);
    }

    patchTeam(teamId, body) {
        const endpoint = `/teams/${teamId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, body);
    }

    getNetworkInfo(networkId) {
        const endpoint = `/networks/${networkId}`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    getNetworkAnalyses(networkId) {
        const endpoint = `/networks/${networkId}/analyses`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    getCoolingNetworkReport(networkId, queryParams) {
        const endpoint = `/networks/${networkId}/report/cooling`;
        return this.axiosAuthentifiedRequest.get(endpoint, {
            responseType: 'blob',
            params: queryParams,
        });
    }

    getClients(queryParams) {
        const endpoint = '/clients';
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }

    getClientInfo(clientId, queryParams) {
        const endpoint = `/clients/${clientId}`;
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }

    getClientSites(clientId, queryParams) {
        const endpoint = `/clients/${clientId}/sites`;
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }

    getClientProduct(clientId, productId, queryParams) {
        const endpoint = `/clients/${clientId}/products/${productId}`;
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }

    addClient(bodyParams) {
        const endpoint = '/clients';
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams);
    }

    removeClient(clientId) {
        const endpoint = `/clients/${clientId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    addSite(clientId, bodyParams) {
        const endpoint = `/clients/${clientId}/sites`;
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams);
    }

    addClientTeam(clientId, bodyParams) {
        const endpoint = `/clients/${clientId}/team`;
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams);
    }

    updateClientInfo(clientId, bodyParams) {
        const endpoint = `/clients/${clientId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    updateClientTeam(clientId, bodyParams) {
        const endpoint = `/clients/${clientId}/team`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    updateClientStatus(clientId, bodyParams) {
        const endpoint = `/clients/${clientId}/status`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    getClientMembers(clientId, queryParams) {
        const getClientMembers = `/clients/${clientId}/members`;

        return this.axiosAuthentifiedRequest.get(getClientMembers, {
            params: queryParams,
        });
    }

    deleteClientMember(clientId, memberId) {
        const endpoint = `/clients/${clientId}/members/${memberId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    getSites(queryParams) {
        const params = { ...queryParams };

        if (params && params.sortKey === 'siteName') {
            params.order = `name_${params.sortOrder.toLowerCase()}`;
        }

        return this.axiosAuthentifiedRequest.get('sites', { params });
    }

    removeSite(siteId) {
        const endpoint = `/sites/${siteId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    getSiteInfo(siteId) {
        const endpoint = `/sites/${siteId}`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    getSiteTree(siteId, queryParams) {
        const endpoint = `/sites/${siteId}/nodes/tree`;
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }

    reorderSiteTree(siteId, bodyParams) {
        const endpoint = `/sites/${siteId}/nodes/reorder`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    getSiteProducts(siteId, queryParams) {
        const endpoint = `/sites/${siteId}/products`;
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }

    getSiteProduct(siteId, productId, queryParams) {
        const endpoint = `/sites/${siteId}/products/${productId}`;
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }

    updateSiteProduct(siteId, productId, bodyParams) {
        const endpoint = `/sites/${siteId}/products/${productId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    deleteSiteProduct(siteId, productId) {
        const endpoint = `/sites/${siteId}/products/${productId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    updateSite(siteId, site) {
        const endpoint = `/sites/${siteId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, site);
    }

    associateSiteProduct(siteId, bodyParams) {
        const endpoint = `/sites/${siteId}/products`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    getSiteProcedures(siteId, queryParams) {
        const endpoint = `/procedures/${siteId}/associateProcedure`;
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }

    associateSiteProcedure(siteId, bodyParams) {
        const endpoint = `/procedures/${siteId}/associateProcedure`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    dissociateSiteProcedure(siteId, procedureId) {
        const endpoint = `/procedures/${siteId}/associateProcedure/${procedureId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    updateSiteStatus(siteId, bodyParams) {
        const endpoint = `/sites/${siteId}/status`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    exportSiteAnalysisCSV(siteId, queryParams) {
        const endpoint = `/sites/${siteId}/exportAnalysisCSV`;
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
            responseType: 'blob',
        });
    }

    getProducts(queryParams) {
        const endpoint = '/products';
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }

    getProduct(productId, queryParams) {
        const endpoint = `/products/${productId}`;
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }

    addProduct(bodyParams) {
        const endpoint = '/products';
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
    }

    updateProduct(productId, bodyParams) {
        const endpoint = `/products/${productId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
    }

    updateProductStatus(productId, bodyParams) {
        const endpoint = `/products/${productId}/status`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    deleteProduct(productId) {
        const endpoint = `/products/${productId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    getAlertTypes() {
        const endpoint = '/activities/types';
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    getAvailableSiteUsers({ siteId }) {
        const endpoint = `/sites/${siteId}/allusers`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    getSiteAlerts({ siteId, queryParams }) {
        const endpoint = `/sites/${siteId}/alerts?perpage=${queryParams.perpage}&page=${queryParams.page}`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    addSiteAlerts({ siteId, payload }) {
        const endpoint = `/sites/${siteId}/alerts`;
        return this.axiosAuthentifiedRequest.post(endpoint, payload);
    }

    getSiteAlertById({ siteId, alertId }) {
        const endpoint = `/sites/${siteId}/alerts/${alertId}`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    updateSiteAlert({ siteId, alertId, payload }) {
        const endpoint = `/sites/${siteId}/alerts/${alertId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, payload);
    }

    deleteSiteAlert({ siteId, alertId }) {
        const endpoint = `/sites/${siteId}/alerts/${alertId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    updateSiteAlertSubscription({ siteId, alertId, payload }) {
        const endpoint = `/sites/${siteId}/alerts/${alertId}/subscribe`;
        return this.axiosAuthentifiedRequest.patch(endpoint, payload);
    }

    getSiteInventoryProducts({ siteId }) {
        const endpoint = `/sites/${siteId}/products/inventories`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    getContacts(queryParams) {
        const endpoint = '/contacts';
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }

    getContact(contactId) {
        const endpoint = `/contacts/${contactId}`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    addContact(bodyParams) {
        const endpoint = '/contacts';
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams);
    }

    updateContact(contactId, bodyParams) {
        const endpoint = `/contacts/${contactId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    updateContactRecieveReport(contactId, bodyParams) {
        const endpoint = `/contacts/${contactId}/receiveReport`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    deleteContact(contactId) {
        const endpoint = `/contacts/${contactId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    getRiskAnalyses(queryParams) {
        const endpoint = '/riskanalyses';
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }

    getRiskAnalysis(riskAnalysisId) {
        const endpoint = `/riskanalyses/${riskAnalysisId}`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    addRiskAnalysis(bodyParams) {
        const endpoint = '/riskanalyses';
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams);
    }

    updateRiskAnalysis(riskAnalysisId, bodyParams) {
        const endpoint = `/riskanalyses/${riskAnalysisId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    deleteRiskAnalysis(riskAnalysisId) {
        const endpoint = `/riskanalyses/${riskAnalysisId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    getSentenceCategories(params) {
        const endpoint = '/automaticsentence/categories';
        return this.axiosAuthentifiedRequest.get(endpoint, { params });
    }

    addSentenceCategory(bodyParams) {
        const endpoint = '/automaticsentence/categories';
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams);
    }

    updateSentenceCategory(categoryId, bodyParams) {
        const endpoint = `/automaticsentence/categories/${categoryId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    removeSentenceCategory(categoryId) {
        const endpoint = `/automaticsentence/categories/${categoryId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    getAutomaticSentences(queryParams) {
        const endpoint = '/automaticsentence/sentences';
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }

    addAutomaticSentence(bodyParams) {
        const endpoint = '/automaticsentence/sentences';
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams);
    }

    updateAutomaticSentence(automaticSentenceId, bodyParams) {
        const endpoint = `/automaticsentence/sentences/${automaticSentenceId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    removeAutomaticSentence(automaticSentenceId) {
        const endpoint = `/automaticsentence/sentences/${automaticSentenceId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    getComponentInfo(componentId) {
        const endpoint = `/components/${componentId}`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    getComponentConfigurations(componentId, bodyParams) {
        const endpoint = `/components/${componentId}/configurations`;
        return this.axiosAuthentifiedRequest.get(endpoint, { params: bodyParams });
    }

    getComponentLabAnalysis(componentId, queryParams) {
        const endpoint = `/components/${componentId}/labAnalysisTypes`;
        return this.axiosAuthentifiedRequest.get(endpoint, { params: queryParams });
    }

    getComponentConfiguration(analysisConfigurationId, params = {}) {
        const endpoint = `/analyses/analysisconfigurations/${analysisConfigurationId}`;
        return this.axiosAuthentifiedRequest.get(endpoint, { params });
    }

    addComponentConfiguration(componentId, bodyParams) {
        const endpoint = `/components/${componentId}/configurations`;
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams);
    }

    addMeasureToAnalysis(workOrderId, activityId, payload) {
        const endpoint = `/workorders/${workOrderId}/activity/${activityId}/analysis`;
        return this.axiosAuthentifiedRequest.patch(endpoint, payload);
    }

    addComponentWaterConsumptionConfiguration(componentId, bodyParams) {
        const endpoint = `/components/${componentId}/WaterConsumptionConfigurations`;
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams);
    }

    deleteComponentConfiguration(componentId, configurationId) {
        const endpoint = `/components/${componentId}/configurations/${configurationId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    getComponentsForAnalysisType(siteId, analysisTypeId, componentId) {
        const endpoint = `/sites/${siteId}/analysis/${analysisTypeId}/components/${componentId}`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    updateComponentsForAnalysisFormula(
        siteId,
        analysisConfigurationId,
        bodyParams,
    ) {
        const endpoint = `/sites/${siteId}/analysis/${analysisConfigurationId}`;
        return this.axiosAuthentifiedRequest.put(endpoint, bodyParams);
    }

    getAnalysisRequests(queryParams) {
        const endpoint = '/analysisRequests';
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }

    addAnalysisRequest(bodyParams) {
        const endpoint = '/analysisRequests';
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams);
    }

    updateAnalysisRequest(analysisRequestId, bodyParams) {
        const endpoint = `/analysisRequests/${analysisRequestId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    getAnalysisRequest(analysisRequestId) {
        const endpoint = `/analysisRequests/${analysisRequestId}`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    removeAnalysisRequest(analysisRequestId) {
        const endpoint = `/analysisRequests/${analysisRequestId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    getContainerTypes(queryParams) {
        const endPoint = '/containertypes';
        return this.axiosAuthentifiedRequest.get(endPoint, {
            params: queryParams,
        });
    }


    addContainerType(bodyParams) {
        const endPoint = '/containertypes';
        return this.axiosAuthentifiedRequest.post(endPoint, bodyParams);
    }

    getContainerType(containerTypeId) {
        const endPoint = `/containertypes/${containerTypeId}`;
        return this.axiosAuthentifiedRequest.get(endPoint);
    }

    updateContainer(containerTypeId, bodyParams) {
        const endPoint = `/containertypes/${containerTypeId}`;
        return this.axiosAuthentifiedRequest.patch(endPoint, bodyParams);
    }

    removeContainer(containerTypeId) {
        const endPoint = `/containertypes/${containerTypeId}`;
        return this.axiosAuthentifiedRequest.delete(endPoint);
    }

    getNetworkTypes(queryParams) {
        const endPoint = '/networktypes';
        return this.axiosAuthentifiedRequest.get(endPoint, {
            params: queryParams,
        });
    }

    addNetworkType(bodyParams) {
        const endPoint = '/networktypes';
        return this.axiosAuthentifiedRequest.post(endPoint, bodyParams);
    }

    getNetworkType(networkTypeId) {
        const endPoint = `/networktypes/${networkTypeId}`;
        return this.axiosAuthentifiedRequest.get(endPoint);
    }

    updateNetworkType(networkTypeId, bodyParams) {
        const endPoint = `/networktypes/${networkTypeId}`;
        return this.axiosAuthentifiedRequest.patch(endPoint, bodyParams);
    }

    removeNetworkType(networkTypeId) {
        const endPoint = `/networktypes/${networkTypeId}`;
        return this.axiosAuthentifiedRequest.delete(endPoint);
    }

    getComponentTypes(queryParams) {
        const endPoint = '/componenttypes';
        return this.axiosAuthentifiedRequest.get(endPoint, {
            params: queryParams,
        });
    }

    addComponentType(bodyParams) {
        const endPoint = '/componenttypes';
        return this.axiosAuthentifiedRequest.post(endPoint, bodyParams);
    }

    getComponentType(componentTypeId) {
        const endPoint = `/componenttypes/${componentTypeId}`;
        return this.axiosAuthentifiedRequest.get(endPoint);
    }

    updateComponentType(componentTypeId, bodyParams) {
        const endPoint = `/componenttypes/${componentTypeId}`;
        return this.axiosAuthentifiedRequest.patch(endPoint, bodyParams);
    }

    removeComponentType(componentTypeId) {
        const endPoint = `/componenttypes/${componentTypeId}`;
        return this.axiosAuthentifiedRequest.delete(endPoint);
    }

    getNode(nodeId, nodeType) {
        const endpoint = `${getNodeType(nodeType)}/${nodeId}`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    getNodeTree(nodeId, nodeType, queryParams) {
        const endpoint = `${getNodeType(nodeType)}/${nodeId}/nodes/tree`;
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }

    deleteSiteNode(nodeId, siteId) {
        const endpoint = `/sites/${siteId}/nodes/${nodeId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    restoreSiteNode(nodeId, siteId) {
        const endpoint = `/sites/${siteId}/nodes/${nodeId}/restore`;
        return this.axiosAuthentifiedRequest.patch(endpoint);
    }

    getNetworkAnalysis(activityId, networkId) {
        const endpoint = `activities/${activityId}/analyses/${networkId}`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    addNode(parentNodeId, parentNodeType, params) {
        const endpoint = `${getNodeType(parentNodeType)}/${parentNodeId}/nodes`;
        const bodyParams = cloneDeep(params);

        delete bodyParams.active;

        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams);
    }

    editNode(nodeInfo) {
        const endpoint = `${getNodeType(nodeInfo.nodeTypeToRedirect)}/${nodeInfo.id
            }`;
        return this.axiosAuthentifiedRequest.patch(endpoint, nodeInfo);
    }

    sendFeedback(bodyParams) {
        const endpoint = '/feedback';
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams);
    }

    getAnalysisGroups(queryParams) {
        const endpoint = 'analyses/groups';
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }

    getAnalysisGroup(analysisGroupId, queryParams) {
        const endpoint = `/analyses/groups/${analysisGroupId}`;
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }

    addAnalysisGroup(newAnalysisGroup) {
        const endpoint = 'analyses/groups';

        return this.axiosAuthentifiedRequest.post(endpoint, newAnalysisGroup);
    }

    updateAnalysisGroup(analysisGroupId, bodyParams) {
        const endpoint = `/analyses/groups/${analysisGroupId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    removeAnalysisGroup(analysisGroupId) {
        const endpoint = `/analyses/groups/${analysisGroupId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    updateAnalysisConfig(componentConfigurationId, configurationInfo) {
        const endpoint = `/analyses/analysisconfigurations/${componentConfigurationId}`;

        // PUT not implemented yet
        return this.axiosAuthentifiedRequest.patch(endpoint, configurationInfo);
    }

    getAnalysisCategories(queryParams) {
        const endpoint = '/analyses/categories';
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }

    getAnalysisCategory(categoryId) {
        const endpoint = `/analyses/categories/${categoryId}`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    addAnalysisCategory(bodyParams) {
        const endpoint = '/analyses/categories';
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams);
    }

    updateAnalysisCategory(categoryId, bodyParams) {
        const endpoint = `/analyses/categories/${categoryId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    removeAnalysisCategory(categoryId) {
        const endpoint = `/analyses/categories/${categoryId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    getAnalysisTypes(queryParams) {
        const endpoint = '/analyses/types';
        return this.axiosAuthentifiedRequest.get(endpoint, { params: queryParams });
    }

    getAnalysisType(typeId) {
        const endpoint = `/analyses/types/${typeId}`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    addAnalysisType(bodyParams) {
        const endpoint = '/analyses/types';
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams);
    }

    updateAnalysisType(typeId, bodyParams) {
        const endpoint = `/analyses/types/${typeId}`;
        return this.axiosAuthentifiedRequest.put(endpoint, bodyParams);
    }

    updateAnalysisTypePosition(typeId, bodyParams) {
        const endpoint = `/analyses/types/reorder/${typeId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    getAnalysisTypesTypes(params = { pagination: false }) {
        const endpoint = 'analysisTypes/types';
        return this.axiosAuthentifiedRequest.get(endpoint, { params });
    }

    addAnalysisTypesType(bodyParams) {
        const endpoint = 'analysisTypes/types';
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams);
    }

    getAnalysisTypesType(analysisTypeId) {
        const endpoint = `analysisTypes/types/${analysisTypeId}`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    updateAnalysisTypesType(analysisTypeId, bodyParams) {
        const endpoint = `analysisTypes/types/${analysisTypeId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    removeAnalysisTypesType(analysisTypeId) {
        const endpoint = `analysisTypes/types/${analysisTypeId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    getMeasureHistory(queryParams) {
        const endpoint = '/analyses/measures/history/latest';
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
            cancelToken: source.token,
        });
    }

    getMeasureHistoryAll(queryParams) {
        const endpoint = '/analyses/measures/history/all';
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
            cancelToken: source.token,
        });
    }

    getDefaultAnalyses(queryParams) {
        const endpoint = '/analyses/defaults/';
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }

    getDefaultAnalysesStats(params = {}) {
        const endpoint = '/analyses/defaults/stats/';
        return this.axiosAuthentifiedRequest.get(endpoint, { params });
    }

    addDefaultAnalysis(bodyParams) {
        const endpoint = '/analyses/defaults/';
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams);
    }

    putDefaultAnalysis(bodyParams) {
        const endpoint = '/analyses/defaults/';
        return this.axiosAuthentifiedRequest.put(endpoint, bodyParams);
    }

    deleteDefaultAnalysis(defaultAnalysisTypeId) {
        const endpoint = `/analyses/defaults/${defaultAnalysisTypeId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    getCoupons(queryParams) {
        const endpoint = '/lab/coupons';
        return this.axiosAuthentifiedRequest.get(endpoint, { params: queryParams });
    }

    getCoupon(couponId) {
        const endpoint = `/lab/coupons/${couponId}`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    addCoupon(bodyParams) {
        const endpoint = '/lab/coupons';
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams);
    }

    updateCoupon(couponId, bodyParams) {
        const endpoint = `/lab/coupons/${couponId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    getWorkOrders(queryParams) {
        const endpoint = '/workorders';
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }

    exportWorkOrdersToCSV(queryParams) {
        const endpoint = '/teams/exportWorkordersCSV';
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
            responseType: 'blob',
        });
    }

    unlockWorkOrder(workOrderId) {
        const endpoint = `/workorders/${workOrderId}/unlock`;
        return this.axiosAuthentifiedRequest.patch(endpoint);
    }

    getProductCategories(queryParams) {
        const endpoint = 'products/categories';
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }

    getProductCategory(categoryId) {
        const endpoint = `products/categories/${categoryId}`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    addProductCategory(bodyParams) {
        const endpoint = 'products/categories';
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams);
    }

    updateProductCategory(categoryId, bodyParams) {
        const endpoint = `/products/categories/${categoryId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    removeProductCategory(categoryId) {
        const endpoint = `/products/categories/${categoryId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    updateProductCategoryPosition(categoryId, bodyParams) {
        const endpoint = `/products/categories/${categoryId}/reorder`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    getBilling(queryParams) {
        const endpoint = '/workorders/billing';
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }

    getBillingLab(queryParams) {
        const endpoint = '/lab/billing';
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }

    getBillingAnalysisRequest(labWorkOrderId) {
        const endpoint = `/lab/billing/${labWorkOrderId}`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    billingStatusUpdate(id, bodyParams) {
        const endpoint = `/workorders/${id}/billingStatus`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    billingLabStatusUpdate(id, bodyParams) {
        const endpoint = `/lab/workorders/${id}/billingStatus`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    getWorkOrder(workOrderId) {
        const endpoint = `/workorders/${workOrderId}`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    submitWorkOrder(workOrderId, payload = {}) {
        const endpoint = `/workorders/${workOrderId}/submit`;
        return this.axiosAuthentifiedRequest.put(endpoint, payload);
    }

    deleteWorkOrder(workOrderId) {
        const endpoint = `/workorders/${workOrderId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    getWorkOrderReportPreview(workOrderId) {
        const endpoint = `/workorders/${workOrderId}/summaryreport/preview`;
        return this.axiosAuthentifiedRequest.get(endpoint, {
            responseType: 'blob',
        });
    }

    sendWorkOrderReportToUsers(workOrderId, bodyParams) {
        const endpoint = `/workorders/${workOrderId}/summaryreport/sendEmailToUsers`;
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams);
    }

    updateSummaryReport(workOrderId, bodyParams) {
        const endpoint = `/workorders/${workOrderId}/summaryreport`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    getLabWorkOrders(queryParams) {
        const endpoint = '/lab/workorders';
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }

    submitLabWorkOrder(workOrderId, payload = {}) {
        const endpoint = `/lab/workorders/${workOrderId}/submit`;
        return this.axiosAuthentifiedRequest.put(endpoint, payload);
    }

    addLabWorkOrder(bodyParams) {
        const endpoint = '/lab/workorders';
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams);
    }

    addLabToExistingWorkOrder(bodyParams, labworkOrderId) {
        const endpoint = `/lab/workorders/${labworkOrderId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    getSiteWorkOrdersList(bodyParams) {
        const endpoint = `/lab/workorders/sites/${bodyParams.siteId}`;
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: bodyParams,
        });
    }

    getLabWorkOrder(workOrderId) {
        const endpoint = `/lab/workorders/${workOrderId}`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    updateLabWorkOrder(workOrderId, bodyParams) {
        const endpoint = `/lab/workorders/${workOrderId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    removeLabWorkOrder(workOrderId) {
        const endpoint = `/lab/workorders/${workOrderId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    updateLabWorkOrderStatus(workOrderId, bodyParams) {
        const endpoint = `/lab/workorders/${workOrderId}/status`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    getLabWorkOrderStatus(labWorkOrderId) {
        const endpoint = `/lab/workorders/${labWorkOrderId}/status`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    getLabWorkOrderResults(workOrderId, queryParams) {
        const endpoint = `/lab/workorders/${workOrderId}/results`;
        return this.axiosAuthentifiedRequest.get(endpoint, queryParams ? {
            params: queryParams,
        } : null);
    }

    updateLabWorkOrderAnalysisRequest(
        labWorkOrderId,
        analysisRequestId,
        bodyParams,
    ) {
        const endpoint = `/lab/workorders/${labWorkOrderId}/analysisRequest/${analysisRequestId}`;
        return this.axiosAuthentifiedRequest.put(endpoint, bodyParams);
    }

    removeAnalysisRequestionFromLabWorkOrder(labWorkOrderId, analysisRequestId) {
        const endpoint = `/lab/workorders/${labWorkOrderId}/analysisRequest/${analysisRequestId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    uploadSamplePicture(analysisRequestId, sampleId, bodyParams) {
        const endpoint = `/analysisRequests/${analysisRequestId}/samples/${sampleId}/pictures`;
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams);
    }

    deleteSamplePicture(analysisRequestId, sampleId, pictureType) {
        const endpoint = `/analysisRequests/${analysisRequestId}/samples/${sampleId}/${pictureType}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    sendSampleEmail(analysisRequestId, sampleId) {
        const endpoint = `/analysisRequests/${analysisRequestId}/samples/${sampleId}/rejected`;
        return this.axiosAuthentifiedRequest.post(endpoint);
    }

    updateLabWorkOrderSummaryReport(workOrderId, bodyParams) {
        const endpoint = `/lab/workorders/${workOrderId}/summaryReport`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    getLabWorkOrderPreliminaryReport(labWorkOrderId) {
        const endpoint = `/lab/workorders/${labWorkOrderId}/previewReport`;
        return this.axiosAuthentifiedRequest.get(endpoint, {
            responseType: 'blob',
        });
    }

    getLabWorkOrderWorkSheet(labWorkOrderId) {
        const endpoint = `/lab/workorders/${labWorkOrderId}/workSheet`;
        return this.axiosAuthentifiedRequest.get(endpoint, {
            responseType: 'blob',
        });
    }

    sendLabWorkOrderReportToUsers(workOrderId, bodyParams) {
        const endpoint = `/lab/workorders/${workOrderId}/summaryreport/sendEmailToUsers`;
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams);
    }

    addWorkOrder(bodyParams) {
        const endpoint = '/workorders';
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams);
    }

    addWorkOrderManyAnalysisOnComponent(workOrderId, activityId, bodyParams) {
        const endpoint = `/workorders/${workOrderId}/activity/${activityId}/analysisOnComponent`;
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams);
    }

    removeWorkOrderManyAnalysisOnComponent(workOrderId, activityId, bodyParams) {
        const endpoint = `/workorders/${workOrderId}/activity/${activityId}/analysisOnComponent`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    addWorkOrderAnalysisOnSample(workOrderId, activityId, bodyParams) {
        const endpoint = `/workorders/${workOrderId}/activity/${activityId}/analysisOnSample`;
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams);
    }

    updateWorkOrder(workOrderId, bodyParams) {
        const endpoint = `/workorders/${workOrderId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    reopenWorkOrder(workOrderId) {
        const endpoint = `/workorders/${workOrderId}/reopen`;
        return this.axiosAuthentifiedRequest.patch(endpoint);
    }

    updateWorkOrderActivityAnalysisMeasure(
        workOrderId,
        activityId,
        bodyParams,
    ) {
        const endpoint = `/workorders/${workOrderId}/activity/${activityId}/analysis`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    updateManualColorNorm(bodyParams) {
        const endpoint = `workOrders/analysis/${bodyParams.analysisId}/manualColorNorm`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    getAnalysisMeasure(
        workOrderId,
        activityId,
        analysisId,
    ) {
        const endpoint = `/workorders/${workOrderId}/activity/${activityId}/analysis/${analysisId}`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    getSampleRequests(queryParams) {
        const endpoint = '/lab/samples';
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }

    getFlowDiagrams(networkId, queryParams) {
        const endpoint = `/networks/${networkId}/flowdiagrams`;
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }

    updateFlowDiagram(networkId, flowDiagramId, bodyParams) {
        const endpoint = `/networks/${networkId}/flowdiagrams/${flowDiagramId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
    }

    addFlowDiagram(networkId, bodyParams) {
        const endpoint = `/networks/${networkId}/flowdiagrams`;
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
    }

    deleteFlowDiagram(networkId, flowDiagramId, fileId) {
        const endpoint = `/networks/${networkId}/flowdiagrams/${flowDiagramId}/file/${fileId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    getLegionellaControlCharts(networkId, queryParams) {
        const endpoint = `/networks/${networkId}/legionellaControlCharts`;
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }

    updateLegionellaControlChart(networkId, legionellaControlChartId, bodyParams) {
        const endpoint = `/networks/${networkId}/legionellaControlCharts/${legionellaControlChartId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
    }

    addLegionellaControlChart(networkId, bodyParams) {
        const endpoint = `/networks/${networkId}/legionellaControlCharts`;
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
    }

    deleteLegionellaControlChart(networkId, flowDiagramId, fileId) {
        const endpoint = `/networks/${networkId}/legionellaControlCharts/${flowDiagramId}/file/${fileId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    updateLegionellaControlChartPosition(networkId, legionellaControlChartId, fileId, bodyParams) {
        const endpoint = `/networks/${networkId}/legionellaControlCharts/${legionellaControlChartId}/file/${fileId}/reorder`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    getControlChart(componentId) {
        const endpoint = `/components/${componentId}/controlChart/`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    updateControlChart(componentId, bodyParams) {
        const endpoint = `/components/${componentId}/controlChart/`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    getInventoryActivity(workOrderId, group = 'product') {
        const endpoint = `workorders/${workOrderId}/inventory`;
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: {
                group,
            },
        });
    }

    updateInventoryActivityItem(workOrderId, bodyParams) {
        const endpoint = `workorders/${workOrderId}/inventory`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    getGraphics(siteId) {
        const endpoint = `/sites/${siteId}/graphics`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    addGraphic(siteId, bodyParams) {
        const endpoint = `/sites/${siteId}/graphics`;
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams);
    }

    editGraphic(siteId, graphicId, bodyParams) {
        const endpoint = `/sites/${siteId}/graphics/${graphicId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    deleteGraphic(siteId, graphicId) {
        const endpoint = `/sites/${siteId}/graphics/${graphicId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint, graphicId);
    }

    getSeries(siteId, graphicId) {
        const endpoint = `/sites/${siteId}/graphics/${graphicId}/series`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    addSerie(siteId, graphicId, bodyParams) {
        const endpoint = `/sites/${siteId}/graphics/${graphicId}/series`;
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams);
    }

    updateSerie(siteId, graphicId, serieId, bodyParams) {
        const endpoint = `/sites/${siteId}/graphics/${graphicId}/series/${serieId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    deleteSerie(siteId, graphicId, serieId) {
        const endpoint = `/sites/${siteId}/graphics/${graphicId}/series/${serieId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    async getSeriesData(siteId, graphicId, bodyParams) {
        const endpoint = `/sites/${siteId}/graphics/${graphicId}/data`;
        const { data } = await this.axiosAuthentifiedRequest.get(endpoint, {
            params: bodyParams,
        });
        return data;
    }

    async getSerieData(siteId, graphicId, serieId, bodyParams) {
        const endpoint = `/sites/${siteId}/graphics/${graphicId}/series/${serieId}/data`;
        const { data } = await this.axiosAuthentifiedRequest.get(endpoint, {
            params: bodyParams,
        });
        return data;
    }

    async getSerieDataForDashboard(siteId, bodyParams) {
        const endpoint = `/sites/${siteId}/graphics/dashboard`;
        const { data } = await this.axiosAuthentifiedRequest.get(endpoint, {
            params: bodyParams,
        });
        return data;
    }

    async getGraphicStats(siteId, graphicId) {
        const endpoint = `/sites/${siteId}/graphics/${graphicId}/statistics`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    async getSiteServiceCallStatistics(siteId, queryParams) {
        const endpoint = `/sites/${siteId}/serviceCallStatistics`;
        const { data } = await this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
        return data;
    }

    /**
     * Fetches a list of countries based on the provided filter.
     *
     * @param {string|null} filter - The filter to apply:
     *           - `null` to return all countries.
     *           - `"active"` to return only active countries.
     *           - `"inactive"` to return only inactive countries.
     * @return {Promise} A promise that resolves to the list of countries.
    */
    getCountries(filter = 'all') {
        const endpoint = `/world/countries?filter=${filter}`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    /**
     * @param {String} country
     * @return {Promise}
     */
    getStates(country) {
        const endpoint = '/world/states';
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: { country },
        });
    }

    /**
     * @param {String} country
     * @param {String} state
     * @return {Promise}
     */
    getCities(country, state) {
        const endpoint = '/world/cities';
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: { country, state },
        });
    }

    /**
     * Return the timezone based on lat/lng
     * @param {String} lat
     * @param {String} lng
     * @return {Promise}
     */
    getTimezone(lat, lng) {
        const endpoint = '/world/timezone';
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: { lat, lng },
        });
    }

    /**
     * @return {Promise}
     */
    getVideoCategories(params = { pagination: false }) {
        const endpoint = '/training/categories';
        return this.axiosAuthentifiedRequest.get(endpoint, { params });
    }

    getVideoCategory(id) {
        const endpoint = `/training/categories/${id}`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    addVideoCategory(bodyParams) {
        const endpoint = '/training/categories/';
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams);
    }

    editVideoCategory(videCategoryId, bodyParams) {
        const endpoint = `/training/categories/${videCategoryId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    removeVideoCategory(id) {
        const endpoint = `/training/categories/${id}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    getDevices(queryParams) {
        const endpoint = '/deviceTypes';
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }

    getDeviceById(deviceId) {
        const endpoint = `/deviceTypes/${deviceId}`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    removeDevice(deviceId) {
        const endpoint = `/deviceTypes/${deviceId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    updateDevice(deviceId, bodyParams) {
        const endpoint = `/deviceTypes/${deviceId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    addDevice(bodyParams) {
        const endpoint = '/deviceTypes/';
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams);
    }

    deleteDeviceTypePicture(deviceTypeId) {
        const endPoint = `/deviceTypes/${deviceTypeId}/picture`;
        return this.axiosAuthentifiedRequest.delete(endPoint);
    }

    deleteDeviceFirmware(deviceTypeId) {
        const endPoint = `/deviceTypes/${deviceTypeId}/firmware`;
        return this.axiosAuthentifiedRequest.delete(endPoint);
    }

    /**
     * @return {Promise}
     */
    getTrainings() {
        const endpoint = '/training/videos/';
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    addTraining(bodyParams) {
        const endpoint = '/training/videos/';
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams);
    }

    editTraining(bodyParams) {
        const endpoint = `/training/videos/${bodyParams.id}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    removeTraining(id) {
        const endpoint = `/training/videos/${id}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    /**
     * @param {String} fileId
     * @return {Promise}
     */
    downloadFile(fileId) {
        const endpoint = `/files/${fileId}`;
        return this.axiosAuthentifiedRequest.get(endpoint, {
            responseType: 'blob',
        });
    }

    /**
     * @param {String} fileId
     * @return {Promise}
     */
    async getFileUrl(fileId) {
        const { data } = await this.axiosAuthentifiedRequest.get(
            `/files/${fileId}`,
            {
                responseType: 'blob',
            },
        );

        return URL.createObjectURL(data);
    }

    getCertificates(networkId, queryParams) {
        const endpoint = `/networks/${networkId}/certificates`;
        return this.axiosAuthentifiedRequest.get(endpoint, { params: queryParams });
    }

    getCertificatesHistory(networkId, queryParams) {
        const endpoint = `/networks/${networkId}/certificates/history`;
        return this.axiosAuthentifiedRequest.get(endpoint, { params: queryParams });
    }

    addCertificate(networkId, bodyParams) {
        const endpoint = `/networks/${networkId}/certificates`;
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
    }

    editCertificate(networkId, componentId, bodyParams) {
        const endpoint = `/networks/${networkId}/certificates/${componentId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    deleteCertificate(networkId, certificateId) {
        const endpoint = `/networks/${networkId}/certificates/${certificateId}/`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    getWOTemplates(params) {
        const endpoint = '/workorders/templates';
        return this.axiosAuthentifiedRequest.get(endpoint, { params });
    }

    deleteWorkOrderTemplate(woTemplateId) {
        const endpoint = `/workorders/templates/${woTemplateId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    addWOTemplate(content) {
        const endpoint = 'workorders/templates';
        return this.axiosAuthentifiedRequest.post(endpoint, content);
    }

    editWOTemplate(woTemplateId, content) {
        const endpoint = `/workorders/templates/${woTemplateId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, content);
    }

    duplicateWOTemplate(woTemplateId, content) {
        const endpoint = `/workorders/templates/${woTemplateId}/duplicate`;
        return this.axiosAuthentifiedRequest.post(endpoint, content);
    }

    getAppointments() {
        const endpoint = '/appointments';
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    getMyAppointments() {
        const endpoint = '/appointments/myAppointment';
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    addAppointment(bodyParams) {
        const endpoint = '/appointments';
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams);
    }

    editAppointment(appointmentId, bodyParams) {
        const endpoint = `/appointments/${appointmentId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    deleteAppointment(appointmentId) {
        const endpoint = `/appointments/${appointmentId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    getAppointmentsBySite(siteId, startDate, endDate) {
        const endpoint = `/appointments/${siteId}/appointmentsBySite`;
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: {
                startDate: startDate || null,
                endDate: endDate || null,
            },
        });
    }

    updateCalendarView(memberId, bodyParams) {
        const endpoint = `/members/${memberId}/setCalendarView`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    getNetworkBySite(siteId) {
        const endpoint = `/sites/${siteId}/networksWithAnalyses`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    getWOTemplateInfo(woTemplateId) {
        const endpoint = `/workorders/templates/${woTemplateId}`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    createWOFromTemplate(woTemplateId, bodyParams) {
        const endpoint = `/workorders/templates/${woTemplateId}`;
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams);
    }

    getAllNotifications(queryParams) {
        const endpoint = '/alerts/notifications';
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }

    getAllNotificationsBySite(siteId, queryParams) {
        const endpoint = `/sites/${siteId}/alerts/notifications`;
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }

    getSiteNotificationDetail(siteId, alertId) {
        const endpoint = `/sites/${siteId}/alerts/notifications/${alertId}`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    getNotification(alertId) {
        const endpoint = `/alerts/notifications/${alertId}`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    addItemToOrder(siteId, bodyParams) {
        const endpoint = `/sites/${siteId}/order/`;
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams);
    }

    getOrders(siteId, params = {}) {
        const endpoint = `/sites/${siteId}/order/`;
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params,
        });
    }

    deleteProductFromOrder(siteId, orderId, itemId) {
        const endpoint = `/sites/${siteId}/order/${orderId}/product/${itemId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    cancelOrder(siteId, orderId) {
        const endpoint = `/sites/${siteId}/order/${orderId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    confirmOrder(siteId, orderId) {
        const endpoint = `/sites/${siteId}/order/${orderId}`;
        return this.axiosAuthentifiedRequest.post(endpoint);
    }

    updateComment(siteId, orderId, bodyParams) {
        const endpoint = `/sites/${siteId}/order/${orderId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    changeNotificationStatus(notificationId, bodyParams) {
        const endpoint = `/alerts/notifications/${notificationId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    getUnacknowledgedAlerts() {
        const endpoint = '/alerts/notifications/unacknowledged';
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    getNodeInfo(siteId, nodeId, queryParams) {
        const endpoint = `/sites/${siteId}/techfiles/${nodeId}`;
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }

    updateTechfile(siteId, nodeId, payload) {
        const endpoint = `/sites/${siteId}/techfiles/${nodeId}`;
        return this.axiosAuthentifiedRequest.put(endpoint, payload,
            {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
    }

    getUnits(siteId, lang) {
        const endpoint = `/sites/${siteId}/techfiles/units?lang=${lang}`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    getTreatmentUnits(lang) {
        const endpoint = `/networks/units/treatments?lang=${lang}`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    copySiteTreeFromAnotherSite(siteId, payload) {
        const endpoint = `/sites/${siteId}/copySiteTreeFromAnotherSite`;
        return this.axiosAuthentifiedRequest.post(endpoint, payload);
    }

    getControllers(siteId, queryParams) {
        const endpoint = `/sites/${siteId}/controllers`;
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }

    deleteController(siteId, controllerId) {
        const endpoint = `/sites/${siteId}/controllers/${controllerId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    addController(siteId, payload) {
        const endpoint = `/sites/${siteId}/controllers`;
        return this.axiosAuthentifiedRequest.post(endpoint, payload);
    }

    // for use when probes are full and connected
    editController(siteId, controllerId, payload) {
        const endpoint = `/sites/${siteId}/controllers/${controllerId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, payload);
    }

    editControllerDetails(siteId, controllerId, payload) {
        const endpoint = `/sites/${siteId}/controllersDetails/${controllerId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, payload);
    }

    deleteProbe(siteId, controllerId, probeId) {
        const endpoint = `/sites/${siteId}/controllers/${controllerId}/probes/${probeId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    editProbe(siteId, controllerId, probeId, payload) {
        const endpoint = `/sites/${siteId}/controllers/${controllerId}/probes/${probeId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, payload);
    }

    addProbeToController(siteId, controllerId, payload) {
        const endpoint = `/sites/${siteId}/controllers/${controllerId}/probes`;
        return this.axiosAuthentifiedRequest.post(endpoint, payload);
    }

    getAvaliableProbes(siteId, controllerId) {
        const endpoint = `/sites/${siteId}/controllers/${controllerId}/probes/available`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    linkProductLevel(analysisConfigurationId, payload) {
        const endpoint = `/analyses/analysisconfigurations/${analysisConfigurationId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, payload);
    }

    unlinkProbe(analysisConfigurationId) {
        const endpoint = `/analyses/analysisconfigurations/${analysisConfigurationId}/unlinkProbe`;
        return this.axiosAuthentifiedRequest.patch(endpoint);
    }

    unlinkFlowMeter(analysisConfigurationId) {
        const endpoint = `/analyses/analysisconfigurations/${analysisConfigurationId}/unlinkFlowMeter`;
        return this.axiosAuthentifiedRequest.patch(endpoint);
    }

    unlinkProductLevel(analysisConfigurationId) {
        const endpoint = `/analyses/analysisconfigurations/${analysisConfigurationId}/unlinkProductMeter`;
        return this.axiosAuthentifiedRequest.patch(endpoint);
    }

    unlinkFuelMeter(analysisConfigurationId) {
        const endpoint = `/analyses/analysisconfigurations/${analysisConfigurationId}/unlinkFuelMeter`;
        return this.axiosAuthentifiedRequest.patch(endpoint);
    }

    getLabAnalysis(queryParams) {
        const endpoint = '/lab/analyses';
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }

    getLabAnalysisProvinces(queryParams) {
        const endpoint = '/lab/analyses/provinces';
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }

    changeLabAnalysisStatus(payload) {
        const endpoint = '/lab/analyses';
        return this.axiosAuthentifiedRequest.patch(endpoint, payload);
    }

    updateLabAnalysisInfos(analysisId) {
        const endpoint = `/lab/analyses/${analysisId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint);
    }

    deleteLabAnalysis(analysisId) {
        const endpoint = `/lab/analyses/${analysisId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    downloadXMLFile() {
        const endpoint = '/lab/analyses/download';
        return this.axiosAuthentifiedRequest.post(endpoint);
    }

    getXMLs(queryParams) {
        const endpoint = '/lab/xmlReport';
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }

    addRBQConfirmation(fileId, payload) {
        const endpoint = `lab/xmlReport/${fileId}`;
        return this.axiosAuthentifiedRequest.put(endpoint, payload,
            {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
    }

    deleteRBQConfirmation(fileId) {
        const endpoint = `/lab/xmlReport/${fileId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    addRecommendationToLabWO(labWorkOrderId, payload) {
        const endpoint = `/lab/workorders/${labWorkOrderId}/addRecommendation`;
        return this.axiosAuthentifiedRequest.patch(endpoint, payload);
    }

    refreshLabWOReport(labWorkOrderId) {
        const endpoint = `/lab/workorders/${labWorkOrderId}/refreshReport`;
        return this.axiosAuthentifiedRequest.post(endpoint);
    }

    sendToRep(labWorkOrderId, payload) {
        const endpoint = `/lab/workorders/${labWorkOrderId}/sendToRep`;
        return this.axiosAuthentifiedRequest.post(endpoint, payload);
    }

    exportLabWOSample(queryParams) {
        const endpoint = '/lab/samples/exportCSV';
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }

    getLabAnalysisComponentType() {
        const endpoint = '/lab/analyses/components';
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    getGlycolTypes() {
        const endpoint = '/lab/samples/glycol/types';
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    getUsersCountByRole(teamId) {
        const endpoint = `/teams/${teamId}/countByRole`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    getAnalysisRequestsTemplates(siteId, queryParams) {
        const endpoint = `/sites/${siteId}/analysisRequest/template`;
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }

    addAnalysisRequestTemplate(siteId, bodyParams) {
        const endpoint = `/sites/${siteId}/analysisRequest/template`;
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams);
    }

    deleteAnalysisRequestsTemplates(siteId, templateId) {
        const endpoint = `/sites/${siteId}/analysisRequest/template/${templateId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    editAnalysisRequestTemplate(siteId, templateId, body) {
        const endpoint = `/sites/${siteId}/analysisRequest/template/${templateId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, body);
    }

    duplicateAnalysisRequestsTemplates(siteId, templateId) {
        const endpoint = `/sites/${siteId}/analysisRequest/template/${templateId}/duplicate`;
        return this.axiosAuthentifiedRequest.post(endpoint);
    }

    startAnalysisRequestsTemplates(siteId, templateId) {
        const endpoint = `/sites/${siteId}/analysisRequest/template/${templateId}/start`;
        return this.axiosAuthentifiedRequest.post(endpoint);
    }

    getARTemplateByComponent(siteId, componentId) {
        const endpoint = `/sites/${siteId}/component/${componentId}/template`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    addChartAction(bodyParams) {
        const endpoint = '/chartActions';
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams);
    }

    getChartsAction(queryParams) {
        const endpoint = '/chartActions';
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }

    getTreatmentName(siteId, componentId, queryParams) {
        const endpoint = `/sites/${siteId}/component/${componentId}/treatement`;
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: queryParams,
        });
    }

    editChartAction(chartId, bodyParams) {
        const endpoint = `/chartActions/${chartId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    deleteChartAction(chartId) {
        const endpoint = `/chartActions/${chartId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    updateFlowDiagramPosition(networkId, flowDiagramId, fileId, bodyParams) {
        const endpoint = `/networks/${networkId}/flowdiagrams/${flowDiagramId}/file/${fileId}/reorder`;
        return this.axiosAuthentifiedRequest.patch(endpoint, bodyParams);
    }

    getSalesMemberByClient(clientId, bodyParams) {
        const endpoint = `/clients/${clientId}/salesMembers/`;
        return this.axiosAuthentifiedRequest.get(endpoint, bodyParams);
    }


    getSalesGroupLeadersByClient(clientId, bodyParams) {
        const endpoint = `/clients/${clientId}/salesGroupLeaders/`;
        return this.axiosAuthentifiedRequest.get(endpoint, bodyParams);
    }

    getFeatures(params) {
        const endpoint = '/features';
        return this.axiosAuthentifiedRequest.get(endpoint, { params });
    }

    getAccess(teamId) {
        const endpoint = `/accessapi/user/access?teamId=${teamId}`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    // Editer l'url et le token dans le menu Register de ERP
    editRegisteredFeature(payload) {
        const endpoint = '/accessapi/register';
        return this.axiosAuthentifiedRequest.patch(endpoint, payload);
    }

    // Get notre url actuelle et son token
    getRegisteredOwnToken(teamId) {
        const endpoint = `/accessapi/register?teamId=${teamId}`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    // Créer un nouveau token d'acces dans API Key Generator de ERP {
    addAccess(payload) {
        const endpoint = '/accessapi/user/access';
        return this.axiosAuthentifiedRequest.post(endpoint, payload);
    }

    // Delete un token
    deleteAccess(apiKeyId, teamId) {
        const endpoint = `/accessapi/user/access?apiKeyId=${apiKeyId}&teamId=${teamId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    // Edit un token
    editAccess(payload) {
        const endpoint = '/accessapi/user/access';
        return this.axiosAuthentifiedRequest.patch(endpoint, payload);
    }

    regenerateAPIKey(accessId, teamId) {
        const endpoint = `/accessapi/user/access/regenerate?idAccess=${accessId}&teamId=${teamId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint);
    }


    toggleActivationFeature(payload) {
        const endpoint = '/accessapi/user/access/toggle/active';
        return this.axiosAuthentifiedRequest.patch(endpoint, payload);
    }


    getContractsForTeam(payload) {
        const endpoint = '/contracts';
        return this.axiosAuthentifiedRequest.get(endpoint, { params: payload });
    }


    updateFeature(featureId, payload) {
        const endpoint = `/features?id=${featureId}`;
        return this.axiosAuthentifiedRequest.put(endpoint, payload);
    }

    addFeature(payload) {
        const endpoint = '/features';
        return this.axiosAuthentifiedRequest.post(endpoint, payload);
    }

    deleteFeature(featureId) {
        const endpoint = `/features?id=${featureId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    superAdminToggleFeature(feature, team, status) {
        const endpoint = `/features/super-admin/${status ? 'deactivate' : 'activate'}?featureId=${feature}&teamId=${team}`;
        return this.axiosAuthentifiedRequest.put(endpoint);
    }

    getSuperAdminFeatures(params) {
        const endpoint = '/features/super-admin';
        return this.axiosAuthentifiedRequest.get(endpoint, { params });
    }

    /**
     * Toggles the feature for a specific team
     *
     * @param {string} feature - The ID of the feature to toggle.
     * @param {string} team - The ID of the team to toggle the feature for.
     * @param {boolean} status - Indicates whether the feature is currently active or not.
     *
     * @returns {Promise} - A promise that resolves when the toggle request is successful.
     */
    toggleFeatureForTeam(feature, team, status) {
        const endpoint = `/features/${status ? 'deactivate' : 'activate'}?featureId=${feature}&teamId=${team}`;
        return this.axiosAuthentifiedRequest.put(endpoint);
    }

    activateErp(activationData) {
        const endpoint = '/features/user/activate';
        return this.axiosAuthentifiedRequest.put(endpoint, activationData);
    }

    getLabWoGrid(labWorkOrderId) {
        const endpoint = `/lab/workorders/${labWorkOrderId}/grid`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    getUpdateInfo() {
        const endpoint = '/updates/info';
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    getChatApiInfo() {
        const endpoint = '/chat/secret';
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    getChatAccesses() {
        const endpoint = '/chat/accesses';
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    getChatConversations(websiteId, chatName) {
        const endpoint = `/chat/conversations/${websiteId}/${chatName}`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    getChatMessages(websiteId, conversationId) {
        const endpoint = `/chat/messages/${websiteId}/${conversationId}`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    sendChatMessage(websiteId, conversationId, messageData) {
        const endpoint = `/chat/messages/send/${websiteId}/${conversationId}`;
        return this.axiosAuthentifiedRequest.post(endpoint, messageData);
    }

    replyChatMessage(websiteId, conversationId, messageData) {
        const endpoint = `/chat/messages/reply/${websiteId}/${conversationId}`;
        return this.axiosAuthentifiedRequest.post(endpoint, messageData);
    }


    deleteChatMessage(websiteId, conversationId, messageId) {
        const endpoint = `/chat/messages/delete/${websiteId}/${conversationId}/${messageId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }


    editChatMessage(websiteId, conversationId, fingerprint, messageData) {
        const endpoint = `/chat/messages/edit/${websiteId}/${conversationId}/${fingerprint}`;
        return this.axiosAuthentifiedRequest.post(endpoint, messageData);
    }


    composeChatMessage(websiteId, conversationId, messageData) {
        const endpoint = `/chat/messages/compose/${websiteId}/${conversationId}`;
        return this.axiosAuthentifiedRequest.post(endpoint, messageData);
    }


    markChatMessageRead(websiteId, conversationId) {
        const endpoint = `/chat/messages/markRead/${websiteId}/${conversationId}`;
        return this.axiosAuthentifiedRequest.post(endpoint);
    }

    markChatMessageDelivered(websiteId, conversationId) {
        const endpoint = `/chat/messages/markDelivered/${websiteId}/${conversationId}`;
        return this.axiosAuthentifiedRequest.post(endpoint);
    }

    getAvailableFlowMeters(siteId) {
        const endpoint = `sites/${siteId}/devices/availableFlowMeters/`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    addFlowMeterHub(siteId, params) {
        const endpoint = `sites/${siteId}/devices/metersHubs/`;
        return this.axiosAuthentifiedRequest.post(endpoint, params);
    }

    addFlowMeter(siteId, params) {
        const endpoint = `sites/${siteId}/devices/flowMeters/`;
        return this.axiosAuthentifiedRequest.post(endpoint, params);
    }

    getFlowMeter(siteId, flowMeterId) {
        const endpoint = `sites/${siteId}/devices/flowMeters/${flowMeterId}`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    addFuelMeter(siteId, params) {
        const endpoint = `sites/${siteId}/devices/fuelMeters/`;
        return this.axiosAuthentifiedRequest.post(endpoint, params);
    }

    getFlowMetersHubs(siteId) {
        const endpoint = `sites/${siteId}/devices/metersHubs/`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    getFlowMetersHub(siteId, meterHubId) {
        const endpoint = `sites/${siteId}/devices/metersHubs/${meterHubId}`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    fetchLastMeasure(params) {
        const endpoint = 'sites/lastReading';
        return this.axiosAuthentifiedRequest.patch(endpoint, params);
    }

    deleteFlowMetersHub(siteId, meterHubId) {
        const endpoint = `sites/${siteId}/devices/metersHubs/${meterHubId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    editFlowMeterHub(siteId, meterHubId, params) {
        const endpoint = `sites/${siteId}/devices/metersHubs/${meterHubId}`;
        return this.axiosAuthentifiedRequest.put(endpoint, params);
    }

    unlinkAndDeleteFlowMeter(siteId, meterHubId, meterId) {
        const endpoint = `sites/${siteId}/devices/metersHubs/${meterHubId}/deleteMeter/${meterId}`;
        return this.axiosAuthentifiedRequest.put(endpoint);
    }

    updateFlowMeter(siteId, meterId, payload) {
        const endpoint = `sites/${siteId}/devices/flowMeters/${meterId}`;
        return this.axiosAuthentifiedRequest.put(endpoint, payload);
    }

    updateFlowMeterLink(siteId, meterId, payload) {
        const endpoint = `sites/${siteId}/devices/flowMeters/${meterId}/link`;
        return this.axiosAuthentifiedRequest.put(endpoint, payload);
    }

    updateMeterHubConfiguration(siteId, meterHubId, params) {
        const endpoint = `sites/${siteId}/devices/metersHubs/${meterHubId}/linkMeters`;
        return this.axiosAuthentifiedRequest.put(endpoint, params);
    }

    uploadGptDocumentation(params, bodyParams) {
        const endpoint = '/gptDocument';
        return this.axiosAuthentifiedRequest.post(endpoint, bodyParams, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    }

    listGptDocuments(userTeam) {
        const endpoint = '/gptDocument';
        const params = { userTeam };
        return this.axiosAuthentifiedRequest.get(endpoint, { params });
    }

    deleteGptDocument(documentName, folder) {
        const endpoint = '/gptDocument';
        const params = { documentName, folder };
        return this.axiosAuthentifiedRequest.delete(endpoint, { params });
    }

    downloadGPTFile(documentName) {
        const endpoint = '/gptDocument/download';
        const params = { documentName };
        return this.axiosAuthentifiedRequest.get(endpoint, { params, responseType: 'blob' });
    }

    addProductSensorHub(siteId, params) {
        const endpoint = `sites/${siteId}/devices/productMeters/`;
        return this.axiosAuthentifiedRequest.post(endpoint, params);
    }

    getAllProductSensorsHub(siteId) {
        const endpoint = `sites/${siteId}/devices/productMeters/`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    getProductSensorsHub(siteId, productSensorId) {
        const endpoint = `sites/${siteId}/devices/productMeters/${productSensorId}`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    editProductSensorsHub(siteId, productSensorId, params) {
        const endpoint = `sites/${siteId}/devices/productMeters/${productSensorId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, params);
    }

    deleteProductSensorsHub(siteId, productSensorId) {
        const endpoint = `sites/${siteId}/devices/productMeters/${productSensorId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    //     MassBalance endpoints
    getAllMassBalanceConfig(siteId) {
        const endpoint = `sites/${siteId}/massBalance`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    addAllMassBalanceConfig(siteId, params) {
        const endpoint = `sites/${siteId}/massBalance`;
        return this.axiosAuthentifiedRequest.post(endpoint, params);
    }

    getAllSiteNetwork(siteId, massBalanceType) {
        const endpoint = `sites/${siteId}/networkByType`;
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: {
                type: massBalanceType,
            },
        });
    }

    getMassBalanceInfos(workOrderId, massBalanceId) {
        const endpoint = `workorders/${workOrderId}/massBalance/${massBalanceId}/infos`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    updateMassBalanceInfos(workOrderId, massBalanceId, params) {
        const endpoint = `workorders/${workOrderId}/massBalance/${massBalanceId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, params);
    }

    // Massbalance auto calculation
    getCalculatedMassBalance(workOrderId) {
        const endpoint = `/workorders/${workOrderId}/getMassBalances`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    postCalculatedMassBalance(workOrderId) {
        const endpoint = `/workorders/${workOrderId}/calculateMassBalance`;
        return this.axiosAuthentifiedRequest.post(endpoint);
    }

    toggleShowAllPdfInMassBalanceCategory(workOrderId, showAllMassBalanceOnReport) {
        const endpoint = `/workorders/${workOrderId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, { showAllMassBalanceOnReport });
    }

    toggleshowPdfInMeasureMassBalance(workOrderId, massBalanceId, params) {
        const endpoint = `workorders/${workOrderId}/massBalance/${massBalanceId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, params);
    }


    getMassBalanceAnalysis(teamId, type) {
        const endpoint = '/analyses/types/massbalance';
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: {
                team: teamId,
                type,
            },
        });
    }

    getMassBalanceFlowMeterByNetwork(siteId, networkId, massBalanceType) {
        const endpoint = `/sites/${siteId}/devices/flowMetersByNetwork`;
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: {
                networkId,
                type: massBalanceType,
            },
        });
    }

    getMassBalanceAnalysisByComponents(siteId, networkId, analysisTypeId) {
        const endpoint = '/analyses/types/massbalance/components';
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: {
                networkId,
                'analysisTypeIds[]': analysisTypeId,
            },
        });
    }

    getMassBalanceProductByNetwork(siteId, networkId) {
        const endpoint = `/sites/${siteId}/devices/productsByNetwork`;
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: {
                networkId,
            },
        });
    }

    getMassBalanceProductByComponent(siteId, networkId, siteProductId) {
        const endpoint = `/sites/${siteId}/devices/componentsByProduct`;
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: {
                productId: siteProductId,
                networkId,
            },
        });
    }

    getOneMassBalanceConfig(siteId, massBalanceId, params) {
        const endpoint = `sites/${siteId}/massBalance/${massBalanceId}`;
        return this.axiosAuthentifiedRequest.get(endpoint, params);
    }

    editOneMassBalanceConfig(siteId, massBalanceId, params) {
        const endpoint = `sites/${siteId}/massBalance/${massBalanceId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, params);
    }

    deleteOneMassBalanceConfig(siteId, massBalanceId) {
        const endpoint = `sites/${siteId}/massBalance/${massBalanceId}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    // Pricing Management endpoints
    getPricings() {
        const endpoint = '/pricing';
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    getPricing(configId) {
        const endpoint = `/pricing/${configId}`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    addPricing(params) {
        const endpoint = '/pricing';
        return this.axiosAuthentifiedRequest.post(endpoint, params);
    }

    patchPricingConfig(configId, params) {
        const endpoint = `/pricing/${configId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, params);
    }

    getDeviceAnalysisKeys(params) {
        const endpoint = '/deviceAnalysisKeys';
        return this.axiosAuthentifiedRequest.get(endpoint, { params });
    }

    getDeviceAnalysisKey(id) {
        const endpoint = `/deviceAnalysisKeys/${id}`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    addDeviceAnalysisKey(params) {
        const endpoint = '/deviceAnalysisKeys';
        return this.axiosAuthentifiedRequest.post(endpoint, params);
    }

    updateDeviceAnalysisKey(id, params) {
        const endpoint = `/deviceAnalysisKeys/${id}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, params);
    }

    deleteDeviceAnalysisKey(id) {
        const endpoint = `/deviceAnalysisKeys/${id}`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    getAllNetworks(sitesId) {
        const endpoint = `/sites/${sitesId}/allnetworks`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    getComponentsListByNetworkId(sitesId, networkId) {
        const endpoint = `sites/${sitesId}/network/${networkId}/allComponentsByNetwork`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    setE4Config(siteId, massBalanceId, payload) {
        const endpoint = `/sites/${siteId}/massBalance/${massBalanceId}/e4boiler`;
        return this.axiosAuthentifiedRequest.post(endpoint, payload);
    }

    deleteE4Config(siteId, massBalanceId) {
        const endpoint = `/sites/${siteId}/massBalance/${massBalanceId}/e4boiler`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    getE4Config(siteId, massBalanceId) {
        const endpoint = `/sites/${siteId}/massBalance/${massBalanceId}/e4boiler`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    //     Current billing summary
    getCurrentBilling(payload) {
        const endpoint = '/pricing/currentBillingSummary';
        return this.axiosAuthentifiedRequest.get(endpoint, {
            params: payload || {},
        });
    }

    getCurrentBillingDetails(type, teamId, queryParams) {
        const endpoint = `/pricing/currentBillingSummary/${type}/${teamId}?perpage=${queryParams.perpage}&page=${queryParams.page}`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    // E4 Cooling
    setE4CoolingConfig(siteId, massBalanceId, payload) {
        const endpoint = `/sites/${siteId}/massBalance/${massBalanceId}/e4cooling`;
        return this.axiosAuthentifiedRequest.post(endpoint, payload);
    }

    deleteE4Cooling(siteId, massBalanceId) {
        const endpoint = `/sites/${siteId}/massBalance/${massBalanceId}/e4cooling`;
        return this.axiosAuthentifiedRequest.delete(endpoint);
    }

    getE4Cooling(siteId, massBalanceId) {
        const endpoint = `/sites/${siteId}/massBalance/${massBalanceId}/e4cooling`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    //     Terms of use
    getTermsOfUse(params) {
        const endpoint = '/termsOfUse';
        return this.axiosAuthentifiedRequest.get(endpoint, { params });
    }

    updateTermsOfUse(payload) {
        const endpoint = '/termsOfUse';
        return this.axiosAuthentifiedRequest.patch(endpoint, payload);
    }

    // New Dashboard
    dashboardSaveLayout(memberId, siteId, payload) {
        const endpoint = `dashboard/member/${memberId}/site/${siteId}`;
        return this.axiosAuthentifiedRequest.patch(endpoint, payload);
    }

    dashboardGetLayout(memberId, siteId) {
        const endpoint = `/dashboard/member/${memberId}/site/${siteId}`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }

    getEconomies(siteId, startDate, endDate) {
        const endpoint = `/sites/${siteId}/e4economy?startDate=${startDate || ''}&endDate=${endDate || ''}`;
        return this.axiosAuthentifiedRequest.get(endpoint);
    }
}

export default {
    install(Vue) {
        const rqObj = new AqAjaxRequests();
        rqObj.init();
        // Set app headers
        Vue.prototype.$rq = rqObj;
        // Make requests accessible from store
        Vuex.Store.prototype.$rq = rqObj;
    },
};
